<!-- CareManagement_Patients_List.vue -->
<template>
  <div class="flex flex-col" style="height: calc(100vh - 165px);">
    <!-- Day at a Glance -->
    <div class="px-4 py-2.5 border-b border-gray-200 bg-white shrink-0">
      <div class="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1.5">Day at a Glance</div>
      <div class="flex items-center gap-2">
        <div class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-gray-50 border border-gray-200">
          <span class="text-base font-bold text-gray-900">{{ glance.roster }}</span>
          <span class="text-[10px] uppercase tracking-wider text-gray-400">Roster</span>
        </div>
        <div class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-gray-50 border border-gray-200">
          <span class="text-base font-bold text-gray-900">{{ glance.startingCaseload }}</span>
          <span class="text-[10px] uppercase tracking-wider text-gray-400">Starting</span>
        </div>
        <div class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-blue-50 border border-blue-200">
          <span class="text-base font-bold text-blue-700">{{ glance.newCases }}</span>
          <span class="text-[10px] uppercase tracking-wider text-blue-600">New</span>
        </div>
        <div class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-gray-50 border border-gray-200">
          <span class="text-base font-bold text-gray-700">{{ glance.followUps }}</span>
          <span class="text-[10px] uppercase tracking-wider text-gray-400">Follow-Ups</span>
        </div>
        <div class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-green-50 border border-green-200">
          <span class="text-base font-bold text-green-700">{{ glance.completedToday }}</span>
          <span class="text-[10px] uppercase tracking-wider text-green-600">Completed</span>
        </div>
        <div class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-gray-50 border border-gray-200">
          <span class="text-base font-bold text-gray-900">{{ glance.endingCaseload }}</span>
          <span class="text-[10px] uppercase tracking-wider text-gray-400">Ending</span>
        </div>
        <div class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border"
          :class="glance.completionRate >= 50 ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'"
        >
          <span class="text-base font-bold" :class="glance.completionRate >= 50 ? 'text-green-700' : 'text-amber-700'">{{ glance.completionRate }}%</span>
          <span class="text-[10px] uppercase tracking-wider" :class="glance.completionRate >= 50 ? 'text-green-600' : 'text-amber-600'">Rate</span>
        </div>
      </div>
    </div>

    <!-- Entity Filters (shared component) -->
    <Worklist_FilterBar
      ref="entityFilterBar"
      :initial-filters="entityInitialFilters"
      :demographic-filters="[]"
      :hide-clear="true"
      @filter-changed="onEntityFilterChanged"
    >
      <template #filters>
        <!-- Patient Search -->
        <div>
          <label class="block text-xs font-medium text-gray-600 mb-1">Patient</label>
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Name, MBI, DOB, or phone..."
            class="w-44 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <!-- Assigned To -->
        <div>
          <label class="block text-xs font-medium text-gray-600 mb-1">Assigned To</label>
          <select
            v-model="assignedMemberId"
            @change="loadPatients(); loadGlance()"
            class="w-44 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          >
            <option :value="null">All</option>
            <option v-for="member in assignedMembers" :key="member.id" :value="member.id">
              {{ member.name }}
            </option>
          </select>
        </div>

        <!-- Programs / Opportunities (per-tenant noun) -->
        <div>
          <label class="block text-xs font-medium text-gray-600 mb-1">{{ opportunityNounPlural }}</label>
          <SimpleMultiSelect
            v-model="metricIds"
            :options="metricOptions"
            :placeholder="'All ' + opportunityNounPlural"
            @change="loadPatients(); loadGlance()"
          />
        </div>

        <!-- Quick Filters -->
        <div class="flex items-center gap-2 flex-wrap">
          <button
            @click="toggleQuickFilter('overdue')"
            :class="[
              'px-3 py-1.5 text-xs font-medium rounded-full border transition-colors whitespace-nowrap',
              quickFilters.overdue
                ? 'bg-red-500 text-white border-red-500'
                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
            ]"
          >
            Overdue
          </button>
          <button
            @click="toggleQuickFilter('dueToday')"
            :class="[
              'px-3 py-1.5 text-xs font-medium rounded-full border transition-colors whitespace-nowrap',
              quickFilters.dueToday
                ? 'bg-indigo-500 text-white border-indigo-500'
                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
            ]"
          >
            Due Today
          </button>
          <button
            @click="toggleQuickFilter('unassigned')"
            :class="[
              'px-3 py-1.5 text-xs font-medium rounded-full border transition-colors whitespace-nowrap',
              quickFilters.unassigned
                ? 'bg-amber-500 text-white border-amber-500'
                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
            ]"
          >
            Unassigned
          </button>
          <button
            @click="toggleSnoozed"
            :class="[
              'px-3 py-1.5 text-xs font-medium rounded-full border transition-colors whitespace-nowrap',
              showSnoozed
                ? 'bg-blue-500 text-white border-blue-500'
                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
            ]"
          >
            Show Snoozed
          </button>
          <button
            @click="showCompleted = !showCompleted; loadPatients()"
            :class="[
              'px-3 py-1.5 text-xs font-medium rounded-full border transition-colors whitespace-nowrap',
              showCompleted
                ? 'bg-green-500 text-white border-green-500'
                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
            ]"
          >
            Completed
          </button>
        </div>

      </template>
    </Worklist_FilterBar>

    <!-- Secondary filters -->
    <div class="px-4 py-2 flex items-center flex-wrap gap-3 border-b border-gray-200 bg-gray-50">
      <!-- Episode Type -->
      <div>
        <label class="block text-xs font-medium text-gray-600 mb-1">Episode Type</label>
        <SimpleMultiSelect
          v-model="filterEpisodeType"
          :options="episodeTypeOptions"
          placeholder="All"
        />
      </div>

      <!-- Risk/Acuity -->
      <div>
        <label class="block text-xs font-medium text-gray-600 mb-1">Risk/Acuity</label>
        <SimpleMultiSelect
          v-model="filterRiskAcuity"
          :options="riskAcuityOptions"
          placeholder="All"
        />
      </div>

      <!-- Stage -->
      <div>
        <label class="block text-xs font-medium text-gray-600 mb-1">Stage</label>
        <SimpleMultiSelect
          v-model="filterStage"
          :options="stageOptions"
          placeholder="All"
        />
      </div>

      <!-- Status -->
      <div>
        <label class="block text-xs font-medium text-gray-600 mb-1">Status</label>
        <SimpleMultiSelect
          v-model="filterStatus"
          :options="statusOptions"
          placeholder="All"
        />
      </div>

      <!-- Payer / Contract -->
      <div>
        <label class="block text-xs font-medium text-gray-600 mb-1">Payer</label>
        <SimpleMultiSelect
          v-model="filterPayer"
          :options="payerOptions"
          placeholder="All"
        />
      </div>

      <!-- Work Type -->
      <div>
        <label class="block text-xs font-medium text-gray-600 mb-1">Work Type</label>
        <SimpleMultiSelect
          v-model="filterWorkType"
          :options="workTypeOptions"
          placeholder="All"
        />
      </div>

      <!-- Care Plan toggle -->
      <button
        @click="filterCarePlan = filterCarePlan === true ? null : true"
        :class="[
          'px-3 py-1.5 text-xs font-medium rounded-full border transition-colors whitespace-nowrap mt-4',
          filterCarePlan === true
            ? 'bg-green-500 text-white border-green-500'
            : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
        ]"
      >
        Has Care Plan
      </button>

      <div class="ml-auto mt-4 flex items-center gap-2">
        <button
          v-if="hasAnyFilters"
          @click="clearAllFilters"
          class="px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700"
        >
          <FontAwesomeIcon :icon="['fas', 'times']" class="mr-1" />
          Clear All
        </button>
        <button
          @click="saveFilters"
          :disabled="!isFilterDirty"
          :class="[
            'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border transition-colors',
            isFilterDirty
              ? 'border-primary-500 bg-primary-500 text-white hover:opacity-90'
              : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
          ]"
        >
          <FontAwesomeIcon :icon="['fas', 'floppy-disk']" class="text-[10px]" />
          Save Filters
        </button>
        <button
          @click="refreshPatients"
          :disabled="loading"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FontAwesomeIcon :icon="['fas', 'arrows-rotate']" class="text-[10px]" :class="{ 'animate-spin': loading }" />
          Refresh
        </button>
        <ExportMenu :disabled="!filteredPatients.length" :exports="[{ type: 'csv', action: exportPatientsCsv }]" />
      </div>
    </div>



    <!-- Main Content Area -->
    <div class="flex-1 flex overflow-hidden">
      <!-- Left: Patient Table -->
      <div
        :style="{ width: selectedPatient ? splitPct + '%' : '100%' }"
        class="flex flex-col border-r border-gray-200 overflow-y-auto overflow-x-hidden min-w-0"
      >
        <CareManagement_PatientTable
          ref="patientTable"
          :patients="filteredPatients"
          :selected-entity-id="selectedPatient?.entity_id"
          :loading="loading"

          @select-patient="onSelectPatient"
          @open-task="onOpenTask"
          @patients-reassigned="loadPatients"
        />
      </div>

      <!-- Drag handle -->
      <div
        v-if="selectedPatient"
        class="w-1 cursor-col-resize bg-gray-200 hover:bg-primary-400 active:bg-primary-500 transition-colors shrink-0"
        @mousedown="startDrag"
      ></div>

      <!-- Right: Patient Drawer -->
      <div
        v-if="selectedPatient"
        :style="{ width: (100 - splitPct) + '%' }"
        class="flex flex-col overflow-hidden"
      >
          <!-- Patient Drawer -->
          <div class="flex-1 overflow-hidden">
            <Patient_Drawer
              ref="patientDrawer"
              :patient-identifier="selectedPatient.patient_identifier"
              :entity-id="selectedPatient.entity_id"
              :patient-data="selectedPatient"
              :patient-name="selectedPatient.patient_name"
              :enabled-tabs="drawerEnabledTabs"
              :show-header="true"
              :initial-task-id="pendingTaskId"
              :default-tab="drawerDefaultTab"
              metric-category="Care Management"
              @show-message="showMessage"
              @task-updated="loadPatients"
              @close="closeDetail"
            />
          </div>
        </div>
    </div>

    <!-- Toast -->
    <transition
      enter-active-class="transition-opacity duration-200"
      leave-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      leave-to-class="opacity-0"
    >
      <div
        v-if="message"
        :class="[
          'fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-lg z-50 max-w-md',
          message.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
        ]"
      >
        <div class="flex items-start">
          <FontAwesomeIcon
            :icon="['fas', message.type === 'success' ? 'check-circle' : 'exclamation-circle']"
            class="mr-2 mt-0.5"
          />
          <div>
            <div class="font-medium">{{ message.text }}</div>
            <div v-if="message.details" class="text-sm mt-1 opacity-90">{{ message.details }}</div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { canAccessFeature } from '@/utils/access.js';
import { FEATURES } from '@/constants/features.js';
import Patient_Drawer from '@/components/Patient_Drawer/Patient_Drawer.vue';
import Worklist_FilterBar from '@/components/widgetcomponents/Worklist_FilterBar.vue';
import SimpleMultiSelect from '@/components/widgetcomponents/Dropdown_Selector/SimpleMultiSelect.vue';
import CareManagement_PatientTable from './CareManagement_PatientTable.vue';
import { TaskStatus } from '@/constants/instructionTypes';
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue';
import { z } from 'zod';
import { mapGetters } from 'vuex';
import { BrowserStore } from '@/utils/BrowserStore';
import isEqual from 'lodash/isEqual';
import { fetchPatientWorkQueue, fetchPatientWorkQueueGlance } from '@/services/api/patientWorkQueue';

const filterStore = new BrowserStore(
  'cm_patient_list_saved_filters',
  z.object({
    entityActiveFilters: z.array(z.object({
      id: z.number(),
      name: z.string(),
      typeName: z.string(),
    })).default([]),
    entitySelectedType: z.string().nullable().default(null),
    assignedMemberId: z.number().nullable().default(null),
    metricIds: z.array(z.number()).default([]),
    showCompleted: z.boolean().default(false),
    showSnoozed: z.boolean().default(false),
    searchQuery: z.string().default(''),
    quickFilters: z
      .object({
        dueToday: z.boolean().optional(),
        overdue: z.boolean().optional(),
        unassigned: z.boolean().optional(),
      })
      .default({}),
    filterEpisodeType: z.array(z.string()).default([]),
    filterRiskAcuity: z.array(z.string()).default([]),
    filterStage: z.array(z.string()).default([]),
    filterStatus: z.array(z.string()).default([]),
    filterPayer: z.array(z.string()).default([]),
    filterWorkType: z.array(z.string()).default([]),
    filterCarePlan: z.boolean().nullable().default(null),
    myPracticesActive: z.boolean().default(false),
  }),
);

export default {
  name: 'CareManagement_Patients_List',
  components: {
    FontAwesomeIcon,
    Patient_Drawer,
    Worklist_FilterBar,
    SimpleMultiSelect,
    CareManagement_PatientTable,
    ExportMenu,
  },
  props: {
    initialPatientId: { type: Number, default: null },
  },
  watch: {
    initialPatientId(newId) {
      if (newId) this._openDeepLinkedPatient();
    },
  },
  data() {
    const savedFilters = filterStore.load();
    const entityInitialFilters =
      savedFilters.entityActiveFilters.length > 0 || savedFilters.myPracticesActive
        ? {
            filters: savedFilters.entityActiveFilters,
            type: savedFilters.entitySelectedType,
            myPracticesActive: savedFilters.myPracticesActive,
          }
        : null;
    return {
      loading: false,
      allPatients: [],
      glance: {
        roster: 0, startingCaseload: 0, newCases: 0, followUps: 0,
        completedToday: 0, endingCaseload: 0, completionRate: 0,
      },
      availableMetrics: [],
      selectedPatient: null,
      pendingTaskId: null,
      drawerDefaultTab: null,
      message: null,

      // Entity filter state for BrowserStore persistence
      entityActiveFilters: savedFilters.entityActiveFilters,
      entitySelectedType: savedFilters.entitySelectedType,
      myPracticesActive: savedFilters.myPracticesActive,
      entityInitialFilters,

      // Filters (initialized from saved state)
      entityFilters: null,
      assignedMemberId: savedFilters.assignedMemberId,
      assignedMembers: [],
      metricIds: savedFilters.metricIds,
      quickFilters: savedFilters.quickFilters,
      showCompleted: savedFilters.showCompleted,
      showSnoozed: savedFilters.showSnoozed,
      searchQuery: savedFilters.searchQuery,

      // Split pane
      splitPct: parseInt(localStorage.getItem('cm_split_pct') || '50'),

      // Secondary filters (client-side, initialized from saved state)
      filterEpisodeType: savedFilters.filterEpisodeType,
      filterRiskAcuity: savedFilters.filterRiskAcuity,
      filterStage: savedFilters.filterStage,
      filterStatus: savedFilters.filterStatus,
      filterPayer: savedFilters.filterPayer,
      filterWorkType: savedFilters.filterWorkType,
      filterCarePlan: savedFilters.filterCarePlan,

      savedFilterSnapshot: filterStore.hasSavedData()
        ? JSON.parse(JSON.stringify(savedFilters))
        : null,
    };
  },
  computed: {
    ...mapGetters('tenant', ['opportunityNounPlural', 'current_tenant_slug']),
    // Drawer tabs for the CM patient drawer. IHCI additionally gets the "AI
    // Insights" tab (readmission-risk detail) — IHCI is the only tenant with a
    // production readmission-scoring pipeline feeding it. Other tenants keep the
    // base set. Tab id 'insights' is already registered in Patient_Drawer.allTabs.
    drawerEnabledTabs() {
      const base = ['details', 'utilization', 'programs', 'gaps', 'engagement', 'care_plan', 'care_graph', 'care_team'];
      const tabs = this.current_tenant_slug === 'ihci' ? [...base.filter(t => t !== 'care_plan'), 'insights'] : base;
      if (canAccessFeature(FEATURES.MODULE_PROVIDER_MESSAGING, { superuserOverride: false })) tabs.push('messages');
      return tabs;
    },
    filteredPatients() {
      let patients = this.allPatients;

      // Search filter (client-side). Matches name / MBI / DOB, plus phone:
      // when the query contains digits we also match against each row's
      // digits-only `phones` array (attached server-side from Snowflake), so a
      // pasted "(860) 638-8926" finds the patient. Phone match only fires when
      // there are >=3 query digits to avoid a stray digit in a name matching
      // every phone. (Jazmine 6/26 — phone search on the patient list.)
      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase();
        const qDigits = this.searchQuery.replace(/\D/g, '');
        patients = patients.filter(p =>
          (p.patient_name || '').toLowerCase().includes(q) ||
          (p.patient_identifier || '').toLowerCase().includes(q) ||
          (p.patient_dob || p.bene_dob || '').includes(q) ||
          (qDigits.length >= 3 && (p.phones || []).some(ph => ph.includes(qDigits)))
        );
      }

      // Quick filters (client-side)
      if (this.quickFilters.dueToday) {
        const today = new Date().toISOString().slice(0, 10);
        patients = patients.filter(p => (p.tasks || []).some(t => t.due_date === today));
      }
      if (this.quickFilters.overdue) {
        patients = patients.filter(p => (p.num_overdue_tasks || 0) > 0);
      }
      if (this.quickFilters.unassigned) {
        patients = patients.filter(p =>
          p.primary_task && (!p.primary_task.owners || p.primary_task.owners.length === 0)
        );
      }
      if (this.showSnoozed) {
        patients = patients.filter(p =>
          (p.tasks || []).some(t => t.status_id === TaskStatus.SNOOZED),
        );
      }

      // Secondary filters
      if (this.filterEpisodeType.length) {
        patients = patients.filter(p => {
          const types = new Set();
          for (const prog of (p.enrolled_programs || [])) {
            if (prog.episode_type) {
              const ep = (prog.episode_type || '').toUpperCase();
              types.add(ep.includes('INP') ? 'IP' : ep.includes('OBS') ? 'OBS' : ep.includes('ED') ? 'ED' : ep.substring(0, 3));
            }
          }
          if (p.last_adt_event_type) types.add(p.last_adt_event_type);
          return this.filterEpisodeType.some(t => types.has(t));
        });
      }
      if (this.filterRiskAcuity.length) {
        patients = patients.filter(p => this._riskAcuityLabels(p).some(l => this.filterRiskAcuity.includes(l)));
      }
      if (this.filterStage.length) {
        patients = patients.filter(p => {
          const taskLabels = (p.tasks || []).map(t => this._stageLabel(t));
          return this.filterStage.some(label => taskLabels.includes(label));
        });
      }
      if (this.filterStatus.length) {
        patients = patients.filter(p => {
          const statuses = (p.tasks || []).map(t =>
            (t.call_attempts || 0) > 0 ? 'In progress' : 'Not started'
          );
          return this.filterStatus.some(s => statuses.includes(s));
        });
      }
      if (this.filterPayer.length) {
        patients = patients.filter(p => {
          const payers = new Set();
          for (const prog of (p.enrolled_programs || [])) {
            if (prog.payer_name) payers.add(prog.payer_name);
          }
          return this.filterPayer.some(v => payers.has(v));
        });
      }
      if (this.filterWorkType.length) {
        patients = patients.filter(p => {
          const types = (p.work_completed || []).map(w => w.work_type_display_name || w.work_type_name);
          return this.filterWorkType.some(t => types.includes(t));
        });
      }
      if (this.filterCarePlan === true) {
        patients = patients.filter(p => p.has_care_plan);
      }

      return patients;
    },
    stats() {
      const all = this.allPatients;
      const today = new Date().toISOString().slice(0, 10);
      return {
        contacted: all.filter(p => (p.work_completed || []).length > 0).length,
        uncontacted: all.filter(p => (p.work_completed || []).length === 0).length,
        overdue: all.filter(p => (p.num_overdue_tasks || 0) > 0).length,
        dueToday: all.filter(p => {
          const tasks = p.tasks || [];
          return tasks.some(t => t.due_date && t.due_date === today);
        }).length,
      };
    },
    metricOptions() {
      return this.availableMetrics.map(m => ({
        value: m.id,
        label: `${m.name} (${m.patient_count || 0})`
      }));
    },
    currentFilterState() {
      return {
        entityActiveFilters: this.entityActiveFilters,
        entitySelectedType: this.entitySelectedType,
        myPracticesActive: this.myPracticesActive,
        assignedMemberId: this.assignedMemberId,
        metricIds: this.metricIds,
        showCompleted: this.showCompleted,
        showSnoozed: this.showSnoozed,
        searchQuery: this.searchQuery,
        quickFilters: this.quickFilters,
        filterEpisodeType: this.filterEpisodeType,
        filterRiskAcuity: this.filterRiskAcuity,
        filterStage: this.filterStage,
        filterStatus: this.filterStatus,
        filterPayer: this.filterPayer,
        filterWorkType: this.filterWorkType,
        filterCarePlan: this.filterCarePlan,
      };
    },
    isFilterDirty() {
      if (this.savedFilterSnapshot === null) return this.hasAnyFilters;
      return !isEqual(this.currentFilterState, this.savedFilterSnapshot);
    },
    hasSecondaryFilters() {
      return this.filterEpisodeType.length > 0 ||
        this.filterRiskAcuity.length > 0 ||
        this.filterStage.length > 0 ||
        this.filterStatus.length > 0 ||
        this.filterPayer.length > 0 ||
        this.filterWorkType.length > 0 ||
        this.filterCarePlan !== null;
    },
    hasAnyFilters() {
      return this.hasSecondaryFilters ||
        this.entityFilters !== null ||
        this.myPracticesActive ||
        this.assignedMemberId !== null ||
        this.metricIds.length > 0 ||
        this.showCompleted ||
        this.showSnoozed ||
        this.searchQuery !== '' ||
        Object.values(this.quickFilters).some(Boolean);
    },
    episodeTypeOptions() {
      const types = new Set();
      for (const p of this.allPatients) {
        for (const prog of (p.enrolled_programs || [])) {
          if (prog.episode_type) {
            const ep = (prog.episode_type || '').toUpperCase();
            types.add(ep.includes('INP') ? 'IP' : ep.includes('OBS') ? 'OBS' : ep.includes('ED') ? 'ED' : ep.substring(0, 3));
          }
        }
        if (p.last_adt_event_type) types.add(p.last_adt_event_type);
      }
      return [...types].sort().map(t => ({ value: t, label: t }));
    },
    riskAcuityOptions() {
      const labels = new Set();
      for (const p of this.allPatients) {
        for (const l of this._riskAcuityLabels(p)) {
          labels.add(l);
        }
      }
      return [...labels].sort().map(l => ({ value: l, label: l }));
    },
    stageOptions() {
      const labels = new Set();
      for (const p of this.allPatients) {
        for (const t of (p.tasks || [])) {
          labels.add(this._stageLabel(t));
        }
      }
      return [...labels]
        .sort((a, b) => a.localeCompare(b))
        .map(label => ({ value: label, label }));
    },
    statusOptions() {
      return [
        { value: 'Not started', label: 'Not started' },
        { value: 'In progress', label: 'In progress' },
      ];
    },
    payerOptions() {
      const payers = new Set();
      for (const p of this.allPatients) {
        for (const prog of (p.enrolled_programs || [])) {
          if (prog.payer_name) payers.add(prog.payer_name);
        }
      }
      return [...payers].sort().map(p => ({ value: p, label: p }));
    },
    workTypeOptions() {
      const types = new Set();
      for (const p of this.allPatients) {
        for (const w of (p.work_completed || [])) {
          const name = w.work_type_display_name || w.work_type_name;
          if (name) types.add(name);
        }
      }
      return [...types].sort().map(t => ({ value: t, label: t }));
    }
  },
  mounted() {
    this.loadAssignedMembers();
    this.loadPatients();
    this.loadGlance();

    this._onVisible = () => {
      if (document.visibilityState === 'visible' && !this.loading) {
        const staleMs = 5 * 60_000;
        if (!this._lastLoadedAt || Date.now() - this._lastLoadedAt > staleMs) {
          this.loadPatients();
        }
      }
    };
    this._onMessage = (event) => {
      if (event.data?.type === 'clinical-session-saved') {
        this.loadPatients();
      }
    };
    document.addEventListener('visibilitychange', this._onVisible);
    window.addEventListener('message', this._onMessage);
  },

  beforeUnmount() {
    document.removeEventListener('visibilitychange', this._onVisible);
    window.removeEventListener('message', this._onMessage);
  },

  methods: {
    startDrag(e) {
      e.preventDefault();
      const container = e.target.parentElement;
      const onMove = (ev) => {
        const rect = container.getBoundingClientRect();
        let pct = ((ev.clientX - rect.left) / rect.width) * 100;
        pct = Math.max(25, Math.min(75, pct));
        this.splitPct = Math.round(pct);
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        localStorage.setItem('cm_split_pct', String(this.splitPct));
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    saveFilters() {
      const state = this.currentFilterState;
      filterStore.save(state);
      this.savedFilterSnapshot = JSON.parse(JSON.stringify(state));
    },
    clearSecondaryFilters() {
      this.filterEpisodeType = [];
      this.filterRiskAcuity = [];
      this.filterStage = [];
      this.filterStatus = [];
      this.filterPayer = [];
      this.filterWorkType = [];
      this.filterCarePlan = null;
    },
    clearAllFilters() {
      // Clear entity filters via FilterBar
      const bar = this.$refs.entityFilterBar;
      if (bar) bar.clearAll();

      // Clear top-level filters
      this.assignedMemberId = null;
      this.metricIds = [];
      this.showCompleted = false;
      this.showSnoozed = false;
      this.searchQuery = '';
      this.quickFilters = {};

      // Clear secondary filters
      this.clearSecondaryFilters();

      this.loadPatients();
      this.loadGlance();
    },
    earliestDueDate(patient) {
      // Active tasks only (status 1/4/7) — completed tasks (2/3) must not surface
      // a stale due date in the export (mirrors CareManagement_PatientTable, req 1873).
      return (patient.tasks || [])
        .filter(t => [1, 4, 7].includes(t.status_id))
        .map(t => t.due_date).filter(Boolean).sort()[0] || null;
    },
    exportPatientsCsv() {
      const patients = this.filteredPatients;
      if (!patients.length) return;

      const escCsv = (v) => {
        if (v == null) return '';
        const s = String(v);
        return s.includes(',') || s.includes('"') || s.includes('\n')
          ? '"' + s.replace(/"/g, '""') + '"' : s;
      };

      const headers = [
        'Patient Name', 'MBI', 'DOB', 'Practice', 'Provider',
        'Payer / Contract', this.opportunityNounPlural, 'Stage', 'Status', 'Risk/Acuity',
        'Due Date', 'Assigned To', 'Work Items', 'Overdue Tasks',
      ];

      const rows = patients.map(p => {
        // Programs column mirrors the table: ALL active programs (filter-independent).
        const programs = (p.all_programs || p.enrolled_programs || []).map(pr => pr.name).join('; ');
        const stage = (p.tasks || []).map(t => t.task_type).join('; ');
        const status = [...new Set((p.tasks || []).map(t =>
          (t.call_attempts || 0) > 0 ? 'In progress' : 'Not started'
        ))].join('; ');
        const riskLabel = this._riskAcuityLabels(p).join(', ');
        const dueDate = this.earliestDueDate(p) || '';
        const owners = (p.tasks || []).flatMap(t => (t.owners || []).map(o => o.name));
        const uniqueOwners = [...new Set(owners)].join('; ');
        const workCount = (p.work_completed || []).length;
        const payer = (p.enrolled_programs || []).map(pr => pr.payer_name).filter(Boolean);
        const uniquePayer = [...new Set(payer)].join('; ');

        return [
          p.patient_name, p.patient_identifier, p.patient_dob || p.bene_dob,
          p.practice_name, p.pcp_name, uniquePayer, programs, stage, status,
          riskLabel, dueDate, uniqueOwners, workCount, p.num_overdue_tasks || 0,
        ].map(escCsv).join(',');
      });

      const csv = headers.join(',') + '\n' + rows.join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cm_patient_list_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
    _stageLabel(task) {
      const tt = task.task_type || '';
      const slug = task.metric_slug || '';
      const attempt = (task.call_attempts || 0) + 1;
      const prefixMap = { VYTALTOC: 'TOC', VYTALPLUS: 'VC+', VYTALPLUSTOC: 'VC+ TOC', CDM: 'CDM' };
      const prefix = prefixMap[slug] || slug;

      if (tt === 'Initial Outreach') {
        return `${prefix} Initial outreach (${attempt})`;
      }
      if (tt === 'Follow-Up') {
        const seq = task.task_seq || 1;
        return `${prefix} Follow-up ${seq} (${attempt})`;
      }
      if (tt === 'VC+ Pre-Screen') {
        return `${prefix} Pre-screen (${attempt})`;
      }
      if (tt === 'VC+ RN Initial') {
        return `${prefix} RN Initial (${attempt})`;
      }
      if (tt === 'VC+ Follow-Up') {
        const seq = task.task_seq || 1;
        return `${prefix} Follow-up ${seq} (${attempt})`;
      }
      if (tt === 'Reach Out to Patient') {
        return `${prefix} Outreach (${attempt})`;
      }
      if (tt === 'Initial Patient Assessment') {
        return `${prefix} Initial Assessment (${attempt})`;
      }
      if (tt === 'Patient Follow-Up') {
        const seq = task.task_seq || 1;
        return `${prefix} Follow-up ${seq} (${attempt})`;
      }
      return prefix ? `${prefix} ${tt}` : tt;
    },
    _riskAcuityLabels(patient) {
      // Risk/Acuity labels for filtering + CSV = the patient's Patient Flag labels
      // (patient.flags, attached by _attach_patient_flags — the same data
      // RiskAcuityBadge renders). The old hardcoded derivation (LACE/ECHO tier,
      // condition_tags cohort chips, metadata.acuity) was REMOVED 2026-07-11:
      // those signals are Patient Flags now, so the filter/CSV read the flags
      // directly and stay consistent with the chips. See performance/patient_flags.
      const labels = (patient.flags || []).map(f => f.label).filter(Boolean);
      return labels.length ? labels : ['—'];
    },
    _riskAcuityLabel(patient) {
      return this._riskAcuityLabels(patient)[0];
    },
    refreshPatients() {
      this.loadPatients();
      this.loadGlance();
    },
    // LAST-REQUEST-WINS, NOT LAST-RESPONSE-WINS.
    //
    // 14 things call loadPatients(), including a visibilitychange handler and a
    // window 'message' handler that fire with no user action. The call takes ~5s
    // on a large caseload (Jen: avg 5.1s, max 40.7s), so overlapping requests are
    // routine — 164 of her 770 calls in the last 7 days started while another was
    // still in flight, and 67 of those pairs FINISHED OUT OF ORDER.
    //
    // Previously the handler assigned `this.allPatients = rowData` unconditionally,
    // so whichever response landed LAST won regardless of which request it belonged
    // to. A stale reply from the pre-filter request would overwrite the filtered
    // list seconds after it rendered — "my filters are changing", and "I see a
    // different list after the initial list is returned" (Jen, 2026-07-30).
    //
    // The fix is a monotonic sequence, not a debounce: debouncing narrows the
    // window but a slow first request still lands late. Only the newest request
    // may write state; older replies are computed, returned and DISCARDED.
    async loadPatients() {
      const seq = (this._loadSeq = (this._loadSeq || 0) + 1);
      this.loading = true;
      try {
        const data = await fetchPatientWorkQueue({
          start_row: 0,
          end_row: 999999,
          entity_filters: this.entityFilters || undefined,
          // ALWAYS send task_owner_id — null means "All", a value means "narrow".
          // Do NOT go back to a conditional spread (`...(x ? {k: x} : {})`):
          // OMITTING the key is not "no owner selected", it is "no owner FILTER",
          // and the backend then returns the whole tenant. That is why Jen saw
          // everyone's patients whenever her selection was momentarily empty —
          // the filter didn't match nothing, it stopped existing (2026-07-28).
          task_owner_id: this.assignedMemberId ?? null,
          filter_args: {
            metric_ids: this.metricIds || [],
            show_completed: this.showCompleted,
            include_snoozed: this.showSnoozed,
          },
        });

        // A newer request has been issued while this one was in flight — its
        // answer is the correct one for the CURRENT filters. Drop this reply.
        if (seq !== this._loadSeq) return;

        if (data?.success) {
          this.allPatients = data.rowData || [];
          this._lastLoadedAt = Date.now();
          if (data.availableMetrics) {
            this.availableMetrics = data.availableMetrics;
          }
        }
      } catch (error) {
        if (seq !== this._loadSeq) return;   // superseded; its error is moot too
        console.error('Error loading patients:', error);
      } finally {
        // Only the newest request may clear the spinner, or a stale reply landing
        // first would show "loaded" while the real request is still running.
        if (seq === this._loadSeq) this.loading = false;
      }
      if (seq !== this._loadSeq) return;
      await this._openDeepLinkedPatient();
    },

    async _openDeepLinkedPatient() {
      if (!this.initialPatientId) return;
      const match = this.allPatients.find(p => p.entity_id === this.initialPatientId);
      // If the patient is already selected AND is in the worklist, just ensure the table
      // is scrolled/paged correctly (covers the race where the initialPatientId watcher
      // fires before loadPatients() completes, sets a minimal selectedPatient via the
      // entity fetch, and then loadPatients() returns but the guard would have blocked
      // the scroll/page-set).
      if (this.selectedPatient?.entity_id === this.initialPatientId) {
        if (match) {
          await this.$nextTick();
          this.$refs.patientTable?.scrollToSelected();
        }
        return;
      }
      if (match) {
        // Wait for the patients watcher (currentPage reset) to flush before setting
        // selectedPatient, so the scroll watcher sees a clean page state.
        await this.$nextTick();
        this.drawerDefaultTab = this.$route?.query?.tab || null;
        this.selectedPatient = match;
        // Belt-and-suspenders: also trigger scroll explicitly from the parent after
        // Vue settles — the child's selectedEntityId watcher handles the common case,
        // but this covers the FilterBar race condition (a second loadPatients call can
        // fire after the watcher and reset currentPage back to 0).
        await this.$nextTick();
        await this.$nextTick();
        this.$refs.patientTable?.scrollToSelected();
      } else {
        // Patient not in today's worklist — fetch entity directly so the drawer can open
        try {
          const { data } = await this.$axios.get(`api/entities/${this.initialPatientId}/`);
          this.drawerDefaultTab = this.$route?.query?.tab || null;
          this.selectedPatient = {
            entity_id: data.id,
            patient_name: data.name,
            patient_identifier: data.identifiers?.BENE_MBI_ID ?? data.name,
          };
        } catch (e) {
          console.error('[CareManagement_Patients_List] could not load patient for deep link:', e);
        }
      }
    },

    async loadAssignedMembers() {
      try {
        const { data } = await this.$axios.get('/api/team-assignments/summary/', {
          params: { metric_ids: this.metricIds.length ? this.metricIds.join(',') : undefined }
        });
        this.assignedMembers = (data || []).map(r => ({
          id: r.member_id,
          name: r.member__name || 'Unknown',
        }));

        // Reconcile the RESTORED selection against the option list.
        // assignedMemberId is rehydrated from BrowserStore on mount, BEFORE this
        // call returns. If the persisted id is no longer a valid member (rules
        // changed, member removed, or a different tenant/metric scope), the
        // <select> binds to a value with no matching <option> and renders blank
        // while still filtering by it — the roster and the visible control
        // disagree. Clear it so the UI and the query always describe the same
        // scope. Intermittent, state-dependent filter behaviour is exactly the
        // "comes and goes" symptom users report and nobody can reproduce.
        if (
          this.assignedMemberId !== null &&
          !this.assignedMembers.some(m => m.id === this.assignedMemberId)
        ) {
          console.warn(
            '[CareManagement_Patients_List] cleared stale assignedMemberId',
            this.assignedMemberId, '— not in member list',
          );
          this.assignedMemberId = null;
          this.loadPatients();
          this.loadGlance();
        }
      } catch (error) {
        console.error('Error loading assigned members:', error);
      }
    },

    onEntityFilterChanged(filters) {
      this.entityFilters = filters.entity_filters || null;

      const bar = this.$refs.entityFilterBar;
      if (bar) {
        this.entityActiveFilters = [...bar.activeFilters];
        this.entitySelectedType = bar.selectedType;
        this.myPracticesActive = bar.myPracticesActive;
      }

      // DELIBERATELY does NOT touch assignedMemberId. "My Practices" scopes
      // PRACTICES; "Assigned To" scopes the ASSIGNEE. They are independent
      // filters and each is owned solely by its own control.
      //   This handler used to auto-SET assignedMemberId when My Practices went
      //   on and auto-CLEAR it when it went off. But it fires on every emit from
      //   Worklist_FilterBar — including the emitChange() the bar makes on MOUNT
      //   when it restores its saved state. So on page load the clear-arm ran
      //   with my_practices undefined and silently wiped a user's own restored
      //   selection: pick your own name, reload, and the filter is back to "All"
      //   without anyone touching it (Jennifer Mulvey, 2026-07-27). Only users
      //   who filtered to THEMSELVES were hit, since the arm compared against
      //   myEntityId. Cross-control state-writing is the root cause, so the
      //   coupling is removed rather than special-cased.

      this.loadPatients();
      this.loadGlance();
    },

    async loadGlance() {
      try {
        const params = {};
        if (this.assignedMemberId) params.member_id = this.assignedMemberId;
        if (this.metricIds.length) params.metric_ids = this.metricIds.join(',');
        const data = await fetchPatientWorkQueueGlance(params);
        this.glance = {
          roster: data.roster || 0,
          startingCaseload: data.starting_caseload || 0,
          newCases: data.new_cases || 0,
          followUps: data.follow_ups || 0,
          completedToday: data.completed_today || 0,
          endingCaseload: data.ending_caseload || 0,
          completionRate: data.completion_rate || 0,
        };
      } catch (e) {
        console.error('Error loading glance:', e);
      }
    },

    toggleQuickFilter(name) {
      this.quickFilters[name] = !this.quickFilters[name];
    },
    toggleSnoozed() {
      this.showSnoozed = !this.showSnoozed;
      this.loadPatients();
    },

    onSelectPatient(patient) {
      this.selectedPatient = patient;
      this.pendingTaskId = null;
      this.drawerDefaultTab = null;
    },

    onOpenTask({ patient, task }) {
      this.pendingTaskId = null;
      this.drawerDefaultTab = 'programs';
      this.selectedPatient = patient;
      this.$nextTick(() => {
        this.pendingTaskId = task.id;
      });
    },

    closeDetail() {
      this.selectedPatient = null;
    },

    showMessage(text, type, details = null) {
      // Accept either positional (text, type) or an object payload
      // { text, type, details } — the drawer pass-through only forwards the
      // first arg, so object form is the reliable shape through the chain.
      if (text && typeof text === 'object') {
        ({ text, type, details = null } = text);
      }
      this.message = { text, type, details };
      setTimeout(() => { this.message = null; }, 5000);
    }
  }
};
</script>
